import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { motion } from "framer-motion";
import { trackEvent } from "../utils/analytics";

// --- CONFIGURATION ---
const MEMBERSHIP_PLANS = [
  {
    name: "Trial Membership",
    price: "Free",
    period: "for 30 days",
    description: "Start with one room and see the full workflow before you commit.",
    cta: "Start Free Trial",
    badge: "No Credit Card Required",
    subtext: "30-day free trial. Keep your Upload Credits when you continue with a membership.",
    highlight: false,
    features: [
      "Unlimited Price Tags",
      "1 Active Sale (Unlimited Archived)",
      "25 Upload Credits",
    ],
  },
  {
    name: "Starter Membership",
    price: "$49",
    period: "/ mo",
    description: "A practical fit for solo operators running light monthly volume.",
    cta: "Start with Starter",
    subtext: "Billed monthly. 100% Upload Credit rollover.",
    highlight: false,
    features: [
      "Unlimited Price Tags",
      "3 Active Sales (Unlimited Archived)",
      "250 Upload Credits / mo",
    ],
  },
  {
    name: "Professional Membership",
    price: "$99",
    period: "/ mo",
    description: "Built for active sale teams pricing, printing, and tracking sales every week.",
    cta: "Start with Professional",
    badge: "Most Popular",
    subtext: "Billed monthly. 100% Upload Credit rollover.",
    highlight: true,
    features: [
      "Unlimited Price Tags",
      "6 Active Sales (Unlimited Archived)",
      "600 Upload Credits / mo",
    ],
  },
  {
    name: "Business Membership",
    price: "$179",
    period: "/ mo",
    description: "For high-volume companies managing multiple active sales at once.",
    cta: "Start with Business",
    subtext: "Billed monthly. 100% Upload Credit rollover.",
    highlight: false,
    features: [
      "Unlimited Price Tags",
      "12 Active Sales (Unlimited Archived)",
      "1,250 Upload Credits / mo",
    ],
  },
];

const CREDIT_PACK_ROWS = [
  { size: "25 Upload Credits", trial: "$10.00", starter: "$7.50", pro: "$5.50", business: "Not offered" },
  { size: "50 Upload Credits", trial: "Not offered", starter: "$15.00", pro: "$11.00", business: "$8.00" },
  { size: "100 Upload Credits", trial: "Not offered", starter: "$30.00", pro: "$22.00", business: "$16.00" },
  { size: "250 Upload Credits", trial: "Not offered", starter: "$75.00", pro: "$55.00", business: "$40.00" },
  { size: "500 Upload Credits", trial: "Not offered", starter: "$150.00", pro: "$110.00", business: "$80.00" },
  { size: "1,000 Upload Credits", trial: "Not offered", starter: "$300.00", pro: "$220.00", business: "$160.00" },
];

const FAQS = [
  {
    question: "What if the AI gets the price or description wrong?",
    answer:
      "You stay in control. Scucci creates a draft, and you can edit or delete any item before printing tags.",
  },
  {
    question: "Do my Upload Credits expire?",
    answer:
      "No. Your Upload Credits roll over month to month while your membership stays active. If you cancel, they stay on your account for two years from your paid-through date.",
  },
  {
    question: "Can I download my inventory?",
    answer:
      "Yes. Export your inventory to CSV anytime for backups, sharing with staff, or importing into other software.",
  },
  {
    question: "Can I cancel my subscription?",
    answer:
      "Yes — cancel anytime from your dashboard. Your membership stays active through the end of your billing period and won’t renew.",
  },
  {
    question: "Can I track my sales?",
    answer:
      "Yes. Scucci is not a point-of-sale system, but Sales Tracker helps you record sold items so you can see what is left and make better markdown decisions.",
  },
];

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const PUBLIC = process.env.PUBLIC_URL || "";
  const [isScrolled, setIsScrolled] = useState(false);
  const isSignedIn = !!user;
  const trackNav = (to, placement) => {
    const suffix = placement ? `__${placement}` : "";
    trackEvent(`nav_pricing_to_${to}${suffix}`);
  };

  // Navigation Helpers
  const goToSignIn = () => navigate("/signin");
  const goToAbout = () => {
    trackNav("about", "header");
    navigate("/about");
  };
  const goPrimary = () => {
    if (isSignedIn) return navigate("/estate-sales-pipeline");
    trackNav("register", "header");
    return navigate("/register");
  };
  const primaryLabel = isSignedIn ? "Go to Dashboard" : "Start Free Trial";
  const headerLabel = isSignedIn ? "Go to Dashboard" : "Get Started";
  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const visiblePlans = isSignedIn
    ? MEMBERSHIP_PLANS.filter((plan) => plan.name !== "Trial Membership")
    : MEMBERSHIP_PLANS;
  useEffect(() => {
    const onScroll = () => {
      setIsScrolled(window.scrollY > 24);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // --- BLOG COVER LOGIC ---

  const [showCreditDetails, setShowCreditDetails] = useState(false);
  const [showAllFeatures, setShowAllFeatures] = useState(false);
  const [showCreditPacks, setShowCreditPacks] = useState(false);
  const [showAllFaqs, setShowAllFaqs] = useState(false);

  return (
    <div className="w-full min-h-screen bg-white font-sans text-slate-900 flex flex-col">
      
      {/* --- HEADER (match Welcome page) --- */}
      <header className="fixed top-0 inset-x-0 z-50">
        <div
          className={
            isScrolled
              ? "absolute inset-0 bg-white/90 backdrop-blur border-b border-gray-200"
              : "absolute inset-0 bg-blue-950/55 backdrop-blur"
          }
        />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 py-2 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link
              to="/"
              onClick={() => trackNav("home", "header")}
              className={
                isScrolled
                  ? "text-blue-950 text-lg font-extrabold tracking-wider"
                  : "text-white text-lg font-extrabold tracking-wider"
              }
              aria-label="Scucci Systems"
            >
              Scucci Systems
            </Link>
            <span
              className={
                isScrolled
                  ? "hidden md:inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-xs font-bold uppercase tracking-wide text-blue-950 border border-blue-100"
                  : "hidden md:inline-flex items-center rounded-full bg-white/10 px-3 py-1 text-xs font-bold uppercase tracking-wide text-white/90"
              }
            >
              Estate Sales
            </span>
          </div>

          <div className="flex items-center gap-2 sm:gap-3 flex-wrap justify-end">
            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => {
                trackNav("home", "header");
                navigate("/");
              }}
              className={
                isScrolled
                  ? "px-3 sm:px-4 py-2 border border-gray-200 text-blue-950 text-xs sm:text-sm font-semibold rounded-xl hover:bg-gray-50 transition"
                  : "px-3 sm:px-4 py-2 border border-white/20 text-white text-xs sm:text-sm font-semibold rounded-xl hover:bg-white/10 transition"
              }
            >
              Home
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.98 }}
              onClick={goToAbout}
              className={
                isScrolled
                  ? "px-3 sm:px-4 py-2 border border-gray-200 text-blue-950 text-xs sm:text-sm font-semibold rounded-xl hover:bg-gray-50 transition"
                  : "px-3 sm:px-4 py-2 border border-white/20 text-white text-xs sm:text-sm font-semibold rounded-xl hover:bg-white/10 transition"
              }
            >
              About
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.98 }}
              onClick={goPrimary}
              className={
                isScrolled
                  ? "inline-flex items-center gap-2 px-4 sm:px-5 py-2 bg-blue-950 text-white text-xs sm:text-sm font-extrabold rounded-xl shadow hover:shadow-md transition"
                  : "inline-flex items-center gap-2 px-4 sm:px-5 py-2 bg-white text-blue-950 text-xs sm:text-sm font-extrabold rounded-xl shadow hover:shadow-md transition"
              }
            >
              {headerLabel}
            </motion.button>

            {!isSignedIn && (
              <motion.button
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.98 }}
                onClick={goToSignIn}
                className={
                  isScrolled
                    ? "px-4 sm:px-5 py-2 border border-gray-200 text-blue-950 text-xs sm:text-sm font-bold rounded-xl hover:bg-gray-50 transition"
                    : "px-4 sm:px-5 py-2 border border-white/25 text-white text-xs sm:text-sm font-bold rounded-xl hover:bg-white/10 transition"
                }
              >
                Sign In
              </motion.button>
            )}
          </div>
        </div>
      </header>

      {/* --- HERO SECTION --- */}
      <div
        className="relative overflow-hidden text-white bg-cover bg-center pt-10"
        style={{ backgroundImage: `url('${PUBLIC}/images/hero/momestate132.jpg')` }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-blue-950/85 via-blue-950/75 to-blue-950/90"></div>
        
        <section className="relative z-10 max-w-5xl mx-auto px-6 pt-16 pb-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-6xl font-extrabold mb-5 leading-tight"
          >
            Simple pricing for estate sale teams
            <span className="block mt-2 text-blue-200/90 text-2xl md:text-4xl leading-snug">
              Choose a membership based on how many sale photos you expect to upload each month.
            </span>
          </motion.h1>
          <p className="mt-3 text-base md:text-lg text-blue-100/90 max-w-3xl mx-auto">
            Printing is unlimited on every plan. Upload Credits are only used when you upload photos, and one photo can produce multiple inventory lines.
          </p>
          <p className="mt-2 text-sm md:text-base text-blue-100/80 max-w-3xl mx-auto">
            Start with one room, see your draft inventory and tags, and choose the plan that fits the way you already work.
          </p>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.35 }}
            className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3"
          >
            <button
              type="button"
              onClick={() => scrollToId("plans")}
              className="px-10 py-3 rounded-xl bg-white text-blue-900 font-extrabold text-base shadow-lg hover:bg-blue-50 transition border border-white/80"
            >
              Compare Plans
            </button>
          </motion.div>
        </section>
      </div>
      

      {/* --- TRUST / PRINTER STRIP --- */}
      <div className="bg-blue-950 text-white/80 py-3 text-center text-sm font-medium relative z-20 border-b border-blue-900">
        <span className="uppercase tracking-wider mr-2 opacity-70">Works with:</span> 
        <span className="mx-2">Brother</span> • <span className="mx-2">Phomemo</span> • <span className="mx-2">Dymo</span> • <span className="mx-2">Zebra</span> • <span className="mx-2">Rollo</span> • <span className="mx-2">Standard Laser/Inkjet</span>
      </div>
      
      {/* --- PRICING MODEL EXPLAINER --- */}
      <section className="max-w-5xl mx-auto px-6 py-10 md:py-12">
        <div className="rounded-[28px] border border-blue-100 bg-blue-50/70 px-6 py-8 md:px-8 md:py-10 shadow-sm text-center">
          <h2 className="text-2xl md:text-3xl font-extrabold text-blue-900">
            How pricing works
          </h2>
          <p className="mt-4 text-base md:text-lg text-gray-700 max-w-3xl mx-auto leading-relaxed">
            Scucci pricing is built around monthly photo volume. Choose a membership based on how many photos you expect to upload. Printing is unlimited on every plan, and unused Upload Credits roll over while your membership stays active.
          </p>
          <div className="mt-6 grid md:grid-cols-3 gap-4 text-left">
            <div className="rounded-2xl border border-blue-100 bg-white px-5 py-5 shadow-sm">
              <div className="text-lg font-extrabold text-blue-900">1. Pick a membership</div>
              <div className="mt-2 text-gray-700">Choose the plan that fits your monthly photo volume.</div>
            </div>
            <div className="rounded-2xl border border-blue-100 bg-white px-5 py-5 shadow-sm">
              <div className="text-lg font-extrabold text-blue-900">2. Upload your photos</div>
              <div className="mt-2 text-gray-700">Upload Credits are only used when you upload photos. One image can still produce multiple inventory items.</div>
            </div>
            <div className="rounded-2xl border border-blue-100 bg-white px-5 py-5 shadow-sm">
              <div className="text-lg font-extrabold text-blue-900">3. Print as much as you need</div>
              <div className="mt-2 text-gray-700">Price tag printing is unlimited on every plan and never uses your Upload Credits.</div>
            </div>
          </div>
        </div>
      </section>

      {/* --- PRICING CARDS --- */}
      <section id="plans" className="max-w-7xl mx-auto px-6 mt-2 relative z-20 mb-16">
        <div className="mb-8 text-center">
          <p className="text-sm md:text-base text-gray-700 max-w-2xl mx-auto">
            Start with the plan that fits your monthly photo volume. You can always move up later if your sales grow.
          </p>
        </div>
        <div className={`grid gap-6 items-start ${isSignedIn ? "lg:grid-cols-3 lg:justify-center" : "lg:grid-cols-4"}`}>
          {visiblePlans.map((plan, idx) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
              viewport={{ once: true }}
              className={`bg-white rounded-2xl shadow-xl p-6 flex flex-col h-full relative border-2 ${
                plan.highlight
                  ? "border-blue-600 ring-4 ring-blue-600/10 scale-105 z-30"
                  : "border-slate-200/80 z-10 opacity-95"
              }`}
            >
              {plan.badge && (
                <div className={`absolute -top-4 left-1/2 -translate-x-1/2 text-white text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-wider shadow-sm ${
                    plan.highlight ? "bg-blue-600" : "bg-green-600"
                }`}>
                  {plan.badge}
                </div>
              )}

              <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2 mt-2 text-center">{plan.name}</h3>
              
              <div className="text-center mb-4">
                <span className="text-4xl md:text-5xl font-extrabold text-blue-900">{plan.price}</span>
                <span className="text-gray-500 font-medium text-sm">{plan.period}</span>
              </div>
              
              <p className="text-sm md:text-base text-gray-600 mb-4 text-center min-h-[2.5rem]">
                {plan.description}
              </p>

              {plan.name === "Trial Membership" && (
                <p className="text-xs md:text-sm font-semibold text-blue-700 text-center mb-4">
                  Start with one room. See your draft inventory and tags before paying for a membership.
                </p>
              )}

              {plan.name === "Professional Membership" && (
                <p className="text-xs md:text-sm font-semibold text-blue-700 text-center mb-4">
                  The best fit for teams running regular sales and printing often.
                </p>
              )}

              {/* FEATURES LIST */}
              <ul className="mb-6 space-y-3">
                {plan.features.map((feature, i) => (
                  <li
                    key={i}
                    className={`flex items-start text-sm md:text-base ${
                      plan.highlight ? "text-gray-700" : "text-gray-600"
                    }`}
                  >
                    <svg className="w-5 h-5 text-blue-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              
              <div className="mt-auto">
                <button
                  type="button"
                  onClick={() => {
                    if (isSignedIn) {
                      navigate("/estate-sales-pipeline/member-maint2");
                      return;
                    }
                    trackNav("register", "plan_card");
                    navigate("/register");
                  }}
                  className={`w-full py-3 rounded-xl font-bold text-center transition block border shadow-sm hover:shadow-md ${
                    plan.highlight 
                      ? "bg-blue-600 text-white hover:bg-blue-700 border-blue-600" 
                      : "bg-blue-50 text-blue-900 hover:bg-blue-100 border-blue-300"
                  }`}
                >
                  {isSignedIn ? "Manage Membership" : plan.cta}
                </button>
                <p className="text-xs md:text-sm text-gray-500 mt-4 text-center">
                  {plan.subtext}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* --- CREDIT DEFINITION --- */}
      <section id="credits" className="max-w-4xl mx-auto px-6 mb-20 text-center">
        <div className="mb-4 max-w-3xl mx-auto text-sm md:text-base text-gray-700">
          <div className="font-semibold text-gray-900">Upload Credits are only used when you upload photos.</div>
          <div className="mt-1">One photo can still produce multiple inventory items, and printing is unlimited on every plan.</div>
        </div>
        <div className="flex justify-center mb-4">
          <button
            onClick={() => setShowCreditDetails(!showCreditDetails)}
            className="text-sm font-semibold text-blue-900 underline underline-offset-4 hover:text-blue-700"
          >
            {showCreditDetails ? "Hide credit details" : "Show exactly how Upload Credits work"}
          </button>
        </div>
        {showCreditDetails && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 md:p-8 inline-block max-w-4xl shadow-sm">
            <h3 className="text-xl font-bold text-blue-900 mb-4">
                How Upload Credits Actually Work
            </h3>
            <div className="flex flex-col md:flex-row items-center justify-center gap-3 md:gap-6 text-lg md:text-2xl font-extrabold text-blue-800 mb-4">
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm border border-blue-100">1 Photo</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm border border-blue-100">Multiple Items</span>
                <span className="bg-white px-4 py-2 rounded-lg shadow-sm border border-blue-100">1 Upload Credit</span>
            </div>
            <p className="text-blue-900/80 text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
                Most photos work best when you focus on a small group of clearly separated items, usually about <strong>3–6</strong> per image. Dense areas may need a few photos instead of one wide shot. <strong>One image. Multiple items. One Upload Credit.</strong>
            </p>
            <p className="text-blue-900/70 text-sm md:text-base mt-3 max-w-2xl mx-auto">
              Unused Upload Credits roll over while your membership stays active. Price tag printing never uses your Upload Credits.
            </p>
          </div>
        )}
      </section>

      {/* --- INCLUDED FEATURES GRID --- */}
      <section className="max-w-6xl mx-auto px-6 mb-20">
        <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-blue-900">What every plan includes</h2>
            <p className="mt-4 text-base md:text-lg text-gray-700 max-w-3xl mx-auto">
              Every membership includes the same core workflow. The difference is monthly photo volume and how many active sales you can keep moving at once.
            </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="p-6 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition">
                <h3 className="font-bold text-lg text-blue-900 mb-2">Price ahead of time</h3>
                <p className="text-gray-600 text-sm">Start pricing before sale day from home, on-site, or anywhere in between. Your team can work from the same sale as soon as photos are ready.</p>
            </div>
            <div className="p-6 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition">
                <h3 className="font-bold text-lg text-blue-900 mb-2">Bulk price tag printing</h3>
                <p className="text-gray-600 text-sm">Print the way you already work: standard sheets, your current label workflow, or Bluetooth printing from your phone.</p>
            </div>
            <div className="p-6 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition">
                <h3 className="font-bold text-lg text-blue-900 mb-2">Draft inventory from photos</h3>
                <p className="text-gray-600 text-sm">Stop typing from scratch. Scucci turns your photos into editable draft inventory so you can move faster before you print.</p>
            </div>
            {showAllFeatures && (
              <>
                <div className="p-6 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition">
                    <h3 className="font-bold text-lg text-blue-900 mb-2">Master price list for missing tags</h3>
                    <p className="text-gray-600 text-sm">When a tag goes missing, staff can look up the item on their phone and keep pricing more consistent during the sale.</p>
                </div>
                <div className="p-6 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition">
                    <h3 className="font-bold text-lg text-blue-900 mb-2">Keep building as the sale grows</h3>
                    <p className="text-gray-600 text-sm">Keep building the sale as it grows without being forced into a different workflow.</p>
                </div>
                <div className="p-6 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition">
                    <h3 className="font-bold text-lg text-blue-900 mb-2">100% credit rollover and activity log</h3>
                    <p className="text-gray-600 text-sm">Unused Upload Credits roll over month to month while your membership stays active, and you can review when credits were used.</p>
                </div>
              </>
            )}
        </div>
        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={() => setShowAllFeatures(!showAllFeatures)}
            className="text-sm font-semibold text-blue-900 underline underline-offset-4 hover:text-blue-700"
          >
            {showAllFeatures ? "Hide extra features" : "Show all included features"}
          </button>
        </div>
      </section>

      {/* --- CREDIT PACK TABLE --- */}
      <section className="bg-slate-100 border-t border-gray-200 py-16">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-extrabold text-blue-900 mb-4">
              Need more Upload Credits later?
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
               Most teams start with their monthly plan and only add more Upload Credits if they need them later.
            </p>
            <div className="mt-4">
              <button
                type="button"
                onClick={() => setShowCreditPacks(!showCreditPacks)}
                className="text-sm font-semibold text-blue-900 underline underline-offset-4 hover:text-blue-700"
              >
                {showCreditPacks ? "Hide pack pricing" : "Show Upload Credit pack pricing"}
              </button>
            </div>
          </div>
          {showCreditPacks && (
            <>
              <div className="overflow-hidden bg-white rounded-xl shadow-lg border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr className="divide-x divide-gray-200">
                      <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-500">Pack Size</th>
                      <th className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider text-gray-500">Trial</th>
                      <th className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider text-gray-500">Starter</th>
                      <th className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider text-blue-700 bg-blue-50">Professional</th>
                      <th className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider text-gray-500">Business</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200 text-sm">
                    {CREDIT_PACK_ROWS.map((row, idx) => (
                      <tr key={row.size} className={`divide-x divide-gray-200 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                        <td className="px-6 py-4 font-bold text-gray-900">{row.size}</td>
                        <td className="px-6 py-4 text-center text-gray-500 font-medium">{row.trial}</td>
                        <td className="px-6 py-4 text-center text-gray-600 font-medium">{row.starter}</td>
                        <td className="px-6 py-4 text-center text-blue-800 font-bold bg-blue-50/50">{row.pro}</td>
                        <td className="px-6 py-4 text-center text-gray-600 font-medium">{row.business}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-center text-sm text-gray-500 mt-4 italic">
                Additional Upload Credit packs are optional and discounted for active members.
              </p>
            </>
          )}
        </div>
      </section>

      {/* --- FAQ SECTION --- */}
      <section id="faq" className="max-w-4xl mx-auto px-6 py-12">
        <h2 className="text-3xl font-extrabold text-blue-900 text-center mb-8">Frequently Asked Questions</h2>
        <p className="text-center text-gray-600 mb-8">A few practical questions before you start.</p>
        <div className="grid gap-6">
            {(showAllFaqs
              ? FAQS
              : FAQS.filter((f) =>
                  [
                    "Do my Upload Credits expire?",
                    "What if the AI gets the price or description wrong?",
                    "Can I track my sales?",
                  ].includes(f.question)
                )
            ).map((faq, i) => (
                <div key={i} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition">
                    <h4 className="font-bold text-lg text-blue-900 mb-2">{faq.question}</h4>
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </div>
            ))}
        </div>
        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={() => setShowAllFaqs(!showAllFaqs)}
            className="text-sm font-semibold text-blue-900 underline underline-offset-4 hover:text-blue-700"
          >
            {showAllFaqs ? "Hide extra FAQs" : "Show all FAQs"}
          </button>
        </div>
        <div className="mt-8 space-y-5">
          <div className="w-full bg-white border border-gray-200 rounded-xl px-6 py-6 text-center shadow-sm">
            <p className="text-base md:text-lg italic text-gray-700 font-serif max-w-3xl mx-auto">
              “They quickly recognized the issues, explained everything clearly, and got our system running smoothly with minimal disruption to our business.”
            </p>
            <p className="text-sm font-bold text-gray-900 mt-4">— Ruth, Ruth’s Reselling (Atlanta)</p>
          </div>

          <div className="w-full bg-blue-50 border border-blue-200 rounded-xl px-6 py-5 text-center">
            <div className="text-blue-900 font-bold text-base">Need help choosing a setup?</div>
            <div className="text-gray-700 mt-2">Reply to <span className="font-semibold">support@scuccisystems.com</span>. A real person will answer.</div>
          </div>
        </div>
      </section>


      {/* --- TRUST/FOOTER STRIP --- */}
      <section className="bg-blue-900 py-12 text-center text-white mt-auto">
        <div className="max-w-4xl mx-auto px-6">
            <div className="mb-8">
                <span className="inline-block px-4 py-1 rounded-full bg-blue-800 border border-blue-700 text-xs font-semibold tracking-wide uppercase mb-4">
                    Built alongside active estate sale operators
                </span>
                <h2 className="text-2xl md:text-3xl font-bold mb-4">
                    Built to solve real sale-day problems
                </h2>
                <p className="text-blue-200 max-w-2xl mx-auto">
                    We are building Scucci alongside real liquidators so it solves the problems you actually deal with before and during sale day.
                </p>
            </div>
          <div className="flex flex-wrap justify-center gap-4">
            <button
              type="button"
              onClick={goPrimary}
              className="px-8 py-3 rounded-xl bg-white text-blue-900 font-bold text-lg hover:bg-blue-50 transition shadow-lg"
            >
              {primaryLabel}
            </button>
          </div>
          <p className="mt-4 text-blue-300 text-sm">Choose a plan, start with one room, and see how quickly Scucci can save you time before sale day.</p>
        </div>
      </section>
      
    </div>
  );
}